import {
  MouseEvent,
  ReactNode,
  KeyboardEvent,
  useId,
  useRef,
  useState,
} from 'react';
import styles from './ListItem.module.scss';
import { useNavigate } from 'react-router-dom';
import { ListItemContent } from './ListItemContent';

interface Props {
  addonEnd?: ReactNode;
  addonStart?: ReactNode;
  description?: string;
  href?: string;
  menu?: ReactNode;
  onClick?: () => void;
  title?: string;
  onAnchorClick?: (event: MouseEvent<HTMLAnchorElement>) => void;
  children?: ReactNode;
}

export function ListItem(props: Props) {
  const id = useId();
  const tabIndex = props.href || props.menu ? 0 : undefined;
  const [menuVisible, setMenuVisible] = useState<boolean>(false);
  const anchorRef = useRef<HTMLAnchorElement>(null);
  const navigate = useNavigate();

  const rootClassNames = [
    styles['list-item'],
    props.href ? styles['list-item-pressable'] : null,
  ]
    .filter(Boolean)
    .join(' ');

  const menuClassNames = [
    styles['list-item-menu'],
    menuVisible && styles['list-item-menu-visible'],
  ]
    .filter(Boolean)
    .join(' ');

  const onClick = () => {
    anchorRef.current?.click();
    if (props.menu) {
      setMenuVisible(true);
    }
    props.onClick?.();
  };

  const onKeyDown = (event: KeyboardEvent<HTMLLIElement>) => {
    if (event.key === 'Enter') {
      onClick();
    }
  };

  const onAnchorClick = (event: MouseEvent<HTMLAnchorElement>) => {
    event.stopPropagation();
    event.preventDefault();
    if (props.onAnchorClick) {
      props.onAnchorClick(event);
    } else if (props.href) {
      navigate(props.href);
    }
  };

  return (
    <li
      aria-labelledby={id}
      className={rootClassNames}
      onClick={onClick}
      onKeyDown={onKeyDown}
      tabIndex={tabIndex}
    >
      {props.addonStart && <div>{props.addonStart}</div>}
      {props.href ? (
        <a
          ref={anchorRef}
          className={styles['list-item-link']}
          href={props.href}
          onClick={onAnchorClick}
          tabIndex={-1}
        >
          <ListItemContent
            description={props.description}
            title={props.title}
          />
        </a>
      ) : (
        <ListItemContent description={props.description} title={props.title}>
          {props.children}
        </ListItemContent>
      )}

      {props.addonEnd && <div>{props.addonEnd}</div>}
    </li>
  );
}
